import { _ as _extends } from '../../_rollupPluginBabelHelpers-mO86zOh3.js';
import { forwardRef, useRef, useEffect } from 'react';
import { keyframes, jsx } from '@emotion/react';
import { themed } from '@indeed/ifl-css';
import { d as durations, e as easings } from '../../index-D7dZz-Df.js';
import { DialogBackdrop } from './DialogBackdrop.js';
import { DialogBase } from './DialogBase.js';
import { useDialogContext } from './context.js';
import { AccessibleTrap } from '../AccessibleTrap/AccessibleTrap.js';
import { Portal } from '../Portal/Portal.js';
import './constants.js';
import '../Popup/Popup.js';
import '@floating-ui/react-dom';
import '../../hooks/useMergeRefs.js';
import '../Box/Box.js';
import '@indeed/ihl-use-id';
import '../../hooks/useControllableState.js';
import '../../utils/warn.js';
import '../../hooks/useIsMobile.js';
import '../../hooks/useWindowSize.js';
import '../../hooks/useIsomorphicLayoutEffect.js';
import '../../hooks/useResponsiveValue.js';
import '../../hooks/useBreakpointIndex.js';
import 'react-remove-scroll';
import '../AccessibleTrap/FocusTrap.js';
import 'react-focus-lock';
import '../../hooks/useInertA11yBackground.js';
import '../../hooks/useForceUpdate.js';
import 'react-dom';
import '../IndeedThemeProvider/IndeedThemeProvider.js';
import '@indeed/ifl-icons';
import '../IndeedThemeProvider/utils.js';
import '../IndeedThemeProvider/theme.js';
import 'deepmerge';
import '@indeed/ifl-themes';
import '../LinkProvider/LinkProvider.js';

/**
 * Dialog wrapper container styles
 */
const baseStyles = _ref => {
  let {
    isFullScreen,
    isMobile
  } = _ref;
  const customCSSProps = [];
  if (!isFullScreen && isMobile) {
    customCSSProps.push({
      paddingBlockStart: 7
    });
  }
  const animFadeIn = keyframes`
    0% { opacity: 0; }
    100% { opacity: 1; }
  `;
  return {
    animation: `${animFadeIn} ${durations.normal} ${easings.linear} forwards`,
    alignItems: !isFullScreen && isMobile ? "flex-end" : "center",
    blockSize: "100%",
    boxSizing: "border-box",
    display: "flex",
    inlineSize: "100%",
    isolation: "isolate",
    justifyContent: "center",
    left: 0,
    opacity: 0,
    paddingBlockEnd: isFullScreen || isMobile ? 0 : 4,
    paddingBlockStart: isFullScreen || isMobile ? 0 : 4,
    paddingInlineEnd: isFullScreen || isMobile ? 0 : 4,
    paddingInlineStart: isFullScreen || isMobile ? 0 : 4,
    position: "fixed",
    top: 0,
    zIndex: 10000,
    "@media (prefers-reduced-motion: reduce)": {
      animation: "none",
      opacity: 1
    },
    ...Object.assign({}, ...customCSSProps)
  };
};
const styleProps = new Set(["isFullScreen", "isMobile", "isOpen"]);
const StyledDialogContent = /*#__PURE__*/themed("div", process.env.NODE_ENV === "production" ? {
  styleProps: Array.from(styleProps),
  shouldForwardProp: prop => !styleProps.has(prop),
  target: "e1dz0cuo0"
} : {
  styleProps: Array.from(styleProps),
  shouldForwardProp: prop => !styleProps.has(prop),
  target: "e1dz0cuo0",
  label: "StyledDialogContent"
})(baseStyles, process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */");
const DialogContent = /*#__PURE__*/forwardRef((_ref2, forwardedRef) => {
  let {
    children,
    ...remainingProps
  } = _ref2;
  const internalRef = useRef(null);
  const {
    accessibleTrapProps,
    id,
    isFullScreen,
    isMobile,
    isOpen,
    portaled,
    setOpen,
    type
  } = useDialogContext();

  // Add Escape key support for dialog closing
  useEffect(() => {
    const onKeyPress = event => {
      if (event.key === "Escape") {
        if (internalRef && internalRef.current) {
          /**
           * Validate that the current active element is within the dialog to avoid early
           * termination with active elements outside the modal (like portals) and
           * validate that current active modal is the topmost modal before closing
           */
          if (internalRef.current.contains(event.target) && !internalRef.current.querySelector("div[role='dialog']")) {
            event.stopImmediatePropagation();
            setOpen(!isOpen);
          }
        }
      }
    };
    const cb = event => onKeyPress(event);
    if (isOpen && type !== "alertdialog") {
      window.addEventListener("keydown", cb);
    }
    return () => {
      window.removeEventListener("keydown", cb);
    };
  }, [isOpen, setOpen, type]);

  // Default dialog wrapper with Focus Trap and Backdrop
  const renderDialogWrapper = () => jsx(AccessibleTrap, _extends({}, accessibleTrapProps?.allowList ? {
    hideBackground: {
      allowList: accessibleTrapProps?.allowList
    }
  } : null, {
    disableScroll: type !== "popup" || isMobile,
    disabled: !isOpen,
    focusWrapperStyle: {
      position: "absolute"
    },
    restoreFocus: true,
    restoreFocusRef: accessibleTrapProps?.restoreFocusRef,
    initializeFocusRef: accessibleTrapProps?.initializeFocusRef ?? internalRef
  }), jsx(StyledDialogContent, _extends({
    id: id,
    isFullScreen: isFullScreen,
    isMobile: isMobile,
    ref: forwardedRef
  }, remainingProps), jsx(DialogBase, {
    ref: internalRef,
    role: type === "alertdialog" ? "alertdialog" : "dialog",
    tabIndex: -1
  }, children), jsx(DialogBackdrop, null)));

  // Conditionally render the component to properly register accessible trap
  return !isOpen ? null : portaled ? jsx(Portal, null, renderDialogWrapper()) : renderDialogWrapper();
});
DialogContent.displayName = "DialogContent";

export { DialogContent, StyledDialogContent, baseStyles };
